import { useCallback, useState } from 'react'

import {
  useDeleteTransactionBatch,
  useRemoveTransactionBatchOperation,
} from '~/hooks/useTransactionBatch'
import { OrgTransaction } from '~/types'

// Server leaves an empty batch behind when the last operation is removed,
// so we explicitly delete it before firing `onBatchEmptied`.
export const useBatchOperationRemover = ({
  transaction,
  onBatchEmptied,
}: {
  transaction: OrgTransaction
  onBatchEmptied: () => void
}) => {
  const { removeBatchOperation, isProcessing: isRemoving } =
    useRemoveTransactionBatchOperation()
  const { deleteBatch, isProcessing: isDeletingBatch } =
    useDeleteTransactionBatch()
  const [removingOperationId, setRemovingOperationId] = useState<
    string | undefined
  >(undefined)

  const handleRemoveOperation = useCallback(
    async (operationId: string) => {
      const isLastOperation = transaction.batchOperations?.length === 1
      setRemovingOperationId(operationId)

      await removeBatchOperation({
        smartAccountId: transaction.smartAccountId,
        operationId,
      })

      if (isLastOperation) {
        await deleteBatch({
          smartAccountId: transaction.smartAccountId,
          transactionId: transaction.id,
        })
        onBatchEmptied()
      }
      setRemovingOperationId(undefined)
    },
    [
      deleteBatch,
      onBatchEmptied,
      removeBatchOperation,
      transaction.batchOperations?.length,
      transaction.id,
      transaction.smartAccountId,
    ],
  )

  return {
    handleRemoveOperation,
    removingOperationId,
    isProcessing: isRemoving || isDeletingBatch,
  }
}
